/* globals THREE */

import { Config } from 'config';

import { forEach } from 'lodash';

import { Base, resolve, Deferred } from 'gear';

import { setObjectPosition, uvFromViewPoint } from './three-util';



function setMaterialTexture( material, textureType, textureMap, texture ) {

  material[textureType+'Map'] = texture;

  if( texture ) {

    const scale  = textureMap ? textureMap.scale : 1;
    const wrap   = textureMap ? textureMap.wrap : 'Stretch';
    const repeat = textureMap && wrap !== 'Stretch' ? textureMap.repeat : 1;

    material[textureType+'Scale'] = textureType === 'normal' ? new THREE.Vector2(scale,scale) : scale;

    texture.wrapS = texture.wrapT = repeat > 1 ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
    texture.repeat.set( repeat, repeat );
    texture.minFilter = repeat > 1 ? THREE.NearestMipMapLinearFilter : THREE.LinearMipMapLinearFilter;

    const map = material[textureType+'MapSlot'];
    if( map ) {
      map.uvTransform = repeat > 1;
      map.uvRepeat.x = repeat;
      map.uvRepeat.y = repeat;
    }

  }
}

// A dummy object to avoid null checks, this could also be used later to
// create a 3D loading animation

export const Object3DBase = Base.extend({
  init() {
    this.addEvents('change');
  },
  animate(/* delta */) {

  }
});


export const EmptyObject3D = Object3DBase.extend({
  dispose() { },
  drawTextures() { },
  draw(/* renderer, camera */){ },
  init() {
    this.bbox = {
      min: { x: -100, y: -100, z: -100 },
      max: { x:  100, y:  100, z:  100 }
    };
  },
  uvFromViewPoint(/* camera, viewPoint, width, height */) {
    return null;
  },
  ready$() {
    return resolve();
  }
});


// Two sided object, using an opaque material for the interior
// We use two steps rendering to be able to avoid z-order artifacts
// The back object is first rendered as a background, then the front object
// is rendered on top effectively making the front object always be on top
// for all visible zones

export const Object3D = Object3DBase.extend({

  Properties: {
    textureView: { type: 'TextureView', owned: false, def: null, set( textureView ) { this._setTextureView(textureView); } },
    geometry: { type: 'geometry', owned: false },

    threeScene: { type: 'THREE.Scene', owned: false, def: null },
  },

  get backSideColor() {
    return this.textureView.backSideColor;
  },

  getThreeTexture( textureType ) {
    return this.textureView.getThreeTexture(textureType);
  },

  // This is called for each animation frame, it will render what is needed
  drawTextures() {
    const { textureView } = this;
    if( textureView && ! textureView.autoDraw ) {
      textureView.draw();
    }
  },

  init( config ) {

    this.callParent(config);

    this._forcedMapReady = null;

    // Inititalize materials
    this._setTextureView( this.textureView );

    this.createThreeObject();
  },

  uvFromViewPoint( camera, viewPoint, width, height ) {
    return uvFromViewPoint( this.object, camera, viewPoint, width, height );
  },

  animate( delta ) {
    if( this.mixer ) {
      this.mixer.update( 0.8 * delta );
      return true;
    }
    return false;
  },

  createThreeObject() {

    const { geometry, threeScene, material, materialBack } = this;

    // Use BufferGeometry, an efficient and GPU friendly data structure
    // This may not be the best for animations, we need to further explore this
    const objectGeometry = Config.useBufferGeometry ? new THREE.BufferGeometry().fromGeometry(geometry) : geometry ;

    const backSideType = this._currentBackSideType = Config.backSide3D !== 'TwoSteps' ? Config.backSide3D :
                         ( this.textureView.model.materials[0].backSide === 'None' ? 'None' : 'TwoSteps' );

    switch( backSideType ) {

      case 'Animation':

        // Make sure we are using a regular Geometry

        const animGeometry = geometry instanceof THREE.BufferGeometry ? new THREE.Geometry().fromBufferGeometry(geometry) : geometry;

        if( animGeometry.bones ) {
          this.object = new THREE.SkinnedMesh( animGeometry, material );
          material.skinning = true;
        }
        else {
          this.object = new THREE.Mesh( animGeometry, material );
        }

        material.morphTargets = true;

        this.mixer = new THREE.AnimationMixer( this.object );
        const { animations, morphTargets } = geometry;
        if( animGeometry.bones && animations && animations.length > 0 ) {
          // Add bones animation
				  const bonesClip = geometry.animations[0];
          const bonesAction = this.mixer.clipAction(bonesClip);
          bonesAction.play();
        }
        if( morphTargets && morphTargets.length > 1 ) {
          const facesClip = THREE.AnimationClip.CreateFromMorphTargetSequence( 'morphAnimation', morphTargets, morphTargets.length-1 );
          const facesAction = this.mixer.clipAction(facesClip);
          facesAction.play();
        }

        break;

      case 'None':

        this.object = new THREE.Mesh( geometry, material );
        break;

      case 'MultiMaterial':

        // This method can produce artifacts. From Three.js r69 onwards the artifacts
        // are more notorious. Some apps may choose to use these option because of performance
        this.object = THREE.SceneUtils.createMultiMaterialObject( objectGeometry, [ material, materialBack ] );
        break;

      default: // 'TwoSteps'

        // This is the best way we found til the moment to avoid back side artifacts
        // We create two objects, one with each material. To render the scene we first
        // hide the main object and then render again with the back side hidden.

        this.objectBack = new THREE.Mesh( objectGeometry, materialBack );

        this.object = new THREE.Mesh( objectGeometry, material);

        break;
    }

    const { object, objectBack } = this;

    objectGeometry.computeBoundingBox();
    const bb = this.bbox = objectGeometry.boundingBox.clone();
    const bbCenter = { x: ( bb.min.x + bb.max.x ) / 2 ,
                       y: ( bb.min.y + bb.max.y ) / 2 ,
                       z: ( bb.min.z + bb.max.z ) / 2 };

    if( objectBack ) {
      setObjectPosition(objectBack,bbCenter);
    }

    setObjectPosition(object,bbCenter);

    bb.min.x -= bbCenter.x; bb.max.x -= bbCenter.x;
    bb.min.y -= bbCenter.y; bb.max.y -= bbCenter.y;
    bb.min.z -= bbCenter.z; bb.max.z -= bbCenter.z;

    if( objectBack ) {
      threeScene.add(objectBack);
    }
    threeScene.add(object);
  },

  dispose() {
    const { threeScene, object, objectBack } = this;

    threeScene.remove(object);
    if( objectBack ) {
      threeScene.remove(objectBack);
    }
  },

  _setTextureView( textureView ) {

    this._textureView = textureView;

    if( ! this.object ){
      this.createMaterialBack();
      this.createMaterial();
    }
    else {
      this.onTextureChange();
      this.onBackSideColorChange();
    }

    textureView.on({
      'drawcomplete': this.invalidateTexture,
      'synctextures': this.onTextureChange
    }, this );
  },

  invalidateTexture() {
    // console.log('drawcomplete');
    this.fireEvent('change');
  },

  onBackSideColorChange() {
    if( this.materialBack ) {
      this.materialBack.color.setHex( this.backSideColor.rgbHex );
    }
  },

  onTextureChange() {
    // console.log('on texture change');
    const { material, materialBack } = this;

    if( ! material ) {
      return;
    }

    material.map = this.getThreeTexture('color');

    let transparent = false;

    if( ! ( Config.tiledBumpMap || Config.tiledNormalMap ) ) {

      forEach(['normal','bump','specular','displacement','alpha'], textureType => {
        const textureMap = this.textureView.getFixedTextureMap(textureType),
              threeTexture = this.getThreeTexture(textureType);

        setMaterialTexture( material, textureType, textureMap, threeTexture );

        if( textureType === 'alpha' ) {
          transparent = true;

          if( materialBack ) {
            setMaterialTexture( materialBack, textureType, textureMap, threeTexture );
          }

          materialBack.needsUpdate = true;
        }
      });

    }
    material.transparent = transparent;
    if( materialBack ) {
      materialBack.transparent = transparent;
    }

    material.needsUpdate = true;
    this.invalidateTexture();
  },

  createMaterial() {

    /*
        this.material = new THREE.MeshLambertMaterial({
          color: 0xf80505,
          side: THREE.FrontSide
        });
        return;
      */

    const materialType = Config.threeMaterialType ? ('Mesh'+Config.threeMaterialType+'Material') : 'MeshPhongMaterial';

    if( materialType === 'MeshPhongMaterial' ) {
      this.material = new THREE[ materialType ]({
        side: THREE.FrontSide,
        shading: THREE.SmoothShading,
        reflectivity: Config.reflectivity || 0,
        specular: 0x000000,
        shininess: Config.shininess || 0
      });
    }
    else {
      this.material = new THREE[ materialType ]({
        side: THREE.FrontSide,
        roughness: Config.roughness ? parseFloat( Config.roughness ) : 0.76,
        metalness: Config.metalness ? parseFloat( Config.metalness ) : 0.0
      });
    }

    // Testing
    const alphaMap = Config.alphaMap;
    if( alphaMap ) {
      // load a texture, set wrap mode to repeat
      const loader = new THREE.TextureLoader();
      loader.crossOrigin = '';
      const texture = loader.load( alphaMap, () => {
        this.invalidateTexture();
      });

      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      const repeatS = Config.tiledBumpMapRepeat || Config.tiledMapRepeat;
      const repeat = repeatS ? parseInt(repeatS) : 16;
      texture.repeat.set( repeat, repeat );

      const setAlphaMap = material => {
        material.alphaMap = texture;
        material.transparent = true;
        material.needsUpdate = true;
        const map = material.alphaMapSlot;
        if( map ) {
          map.uvTransform = true;
          map.uvRepeat.x = repeat;
          map.uvRepeat.y = repeat;
        }

      };

      setAlphaMap(this.material);
      if( this.materialBack ) {
        setAlphaMap(this.materialBack);
      }
    }

    const tiledMap = Config.tiledBumpMap || Config.tiledNormalMap;
    if( tiledMap ) {

      this._forcedMapReady = new Deferred();

      // load a texture, set wrap mode to repeat
      var onLoad = () => {
        this.invalidateTexture();
        this._forcedMapReady.resolve();
      };

      const loader = new THREE.TextureLoader();
      loader.crossOrigin = '';
      const texture = loader.load( tiledMap, onLoad, undefined, onLoad );

      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;

      const repeatS = Config.tiledBumpMapRepeat || Config.tiledMapRepeat;
      const repeat = repeatS ? parseInt(repeatS) : 16;
      texture.repeat.set( repeat, repeat );

      texture.minFilter = THREE.NearestMipMapLinearFilter;

      const { tiledMapScale } = Config;
      const { material } = this;
      let map;
      if( Config.tiledBumpMap ) {
        material.bumpMap = texture;
        if( tiledMapScale ) {
          material.bumpScale = parseFloat(tiledMapScale);
        }
        map = material.bumpMapSlot;
      }
      else {
        material.normalMap = texture;
        if( tiledMapScale ) {
          const scale = parseFloat(tiledMapScale);
          material.normalScale = new THREE.Vector2( scale, scale );
        }
        map = material.normalMapSlot;
      }

      map.uvTransform = true;
      map.uvRepeat.x = repeat;
      map.uvRepeat.y = repeat;
    }

    this.onTextureChange();
  },

  ready$() {
    const deferred = this._forcedMapReady;
    return deferred ? deferred.promise : resolve();
  },

  createMaterialBack() {

    this.materialBack = new THREE.MeshLambertMaterial({
      color: this.backSideColor.rgbHex,
      side: THREE.BackSide
    });
  },

  draw( renderer, camera ){

    const { threeScene, object, objectBack } = this;

    if( this._currentBackSideType === 'TwoSteps' ) {

      objectBack.visible = true;
      object.visible = false;

      renderer.render(threeScene,camera);

      objectBack.visible = false;
      object.visible = true;

      renderer.autoClear = false;
    }

    renderer.render(threeScene,camera);

    renderer.autoClear = true;
  }
});



/** WEBPACK FOOTER **
 ** ./src/ui/canvas3d/object3d.js
 **/